import { Helmet } from 'react-helmet-async';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';

/**
 * Target query/intent: "fantasy football draft assistant" / "AI mock draft". Standalone
 * descriptive page for the live draft assistant tool (distinct from the interactive tool itself,
 * which lives behind the authenticated /draft app route) -- crawlable text content per R2.1,
 * not just an embedded widget.
 */
function DraftAssistantView() {
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>AI Fantasy Football Draft Assistant — RosterRx</title>
        <meta
          name='description'
          content='RosterRx tracks your live snake draft pick by pick and, on Pro, adds AI-powered pick suggestions, round predictions, and mock draft practice against a simulated league.'
        />
        <link rel='canonical' href={`${SITE_URL}/draft-assistant`} />
        <meta property='og:title' content='AI Fantasy Football Draft Assistant — RosterRx' />
        <meta
          property='og:description'
          content='RosterRx tracks your live snake draft pick by pick and, on Pro, adds AI-powered pick suggestions, round predictions, and mock draft practice against a simulated league.'
        />
        <meta property='og:url' content={`${SITE_URL}/draft-assistant`} />
        <meta property='og:type' content='website' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content='AI Fantasy Football Draft Assistant — RosterRx' />
        <meta
          name='twitter:description'
          content='RosterRx tracks your live snake draft pick by pick and, on Pro, adds AI-powered pick suggestions, round predictions, and mock draft practice against a simulated league.'
        />
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>AI Fantasy Football Draft Assistant</h2>
        <p>
          RosterRx's draft assistant tracks your live snake draft pick by pick, so you always know
          who's gone, who's left, and how your roster is shaping up. The free tier covers real-time
          pick tracking for any draft, whether you're drafting inside RosterRx or importing a
          completed Yahoo or ESPN draft afterward.
        </p>
        <p>
          Pro adds three AI-powered tools on top: a <strong>pick suggestion</strong> that
          recommends the best available player for your specific roster needs, a{' '}
          <strong>round prediction</strong> that forecasts what other teams are likely to draft
          before your next turn (factoring in each opponent's tracked draft history and
          tendencies), and <strong>mock draft practice</strong> — a full AI-simulated snake draft
          you can run against a realistic league before draft day counts for real.
        </p>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>How it works</h2>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 8 }}>
            Start a new draft, or import a completed Yahoo or ESPN draft to build manager profiles
            from your league's real history.
          </li>
          <li style={{ marginBottom: 8 }}>
            Consensus rankings from ESPN, Yahoo, and FantasyPros are available in the available-
            players panel throughout the draft, so you're never picking blind.
          </li>
          <li style={{ marginBottom: 8 }}>
            Pro: ask for a pick suggestion on your turn, or a round prediction to see what's likely
            coming before you're on the clock again.
          </li>
          <li>
            Pro: practice with Mock Draft — a full AI-simulated snake draft against a realistic
            league, any time.
          </li>
        </ul>
      </div>
    </div>
  );
}

export default DraftAssistantView;
